import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Broadcast, CircleNotch } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <div className="hidden lg:flex flex-col justify-between w-1/2 border-r border-[#262626] p-14 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-[#3A86FF] flex items-center justify-center rounded-sm">
            <Broadcast size={22} weight="fill" />
          </div>
          <div className="font-mono text-xs tracking-[0.3em] text-[#737373]">REVINE</div>
        </div>
        <div>
          <div className="font-mono text-xs text-emerald-500 mb-4 flex items-center gap-2">
            <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-pulse" /> SYSTEM OPERATIONAL
          </div>
          <h1 className="font-head font-black text-5xl tracking-tight leading-[1.05] mb-5">
            Channel<br />Command<br /><span className="text-[#3A86FF]">Studio</span>
          </h1>
          <p className="text-[#A3A3A3] max-w-md text-sm leading-relaxed">
            Orchestrate professional 24/7 linear streaming channels. Schedule programming blocks,
            generate HLS manifests, and monitor channel health from one operations console.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-4 font-mono text-xs text-[#737373]">
          <div><div className="text-white text-lg font-bold">24/7</div>LINEAR PLAYOUT</div>
          <div><div className="text-white text-lg font-bold">HLS</div>MANIFEST GEN</div>
          <div><div className="text-white text-lg font-bold">LIVE</div>MONITORING</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm ccs-enter">
          <h2 className="font-head font-bold text-2xl tracking-tight mb-1">Sign in</h2>
          <p className="text-sm text-[#737373] mb-8">Access your operations console</p>

          <form onSubmit={submit} className="space-y-4">
            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="login-email" required />
            <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="login-password" required />

            {error && <div data-testid="auth-error" className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-sm">{error}</div>}

            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white font-medium py-2.5 rounded-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <CircleNotch size={16} className="animate-spin" />}
              Sign in
            </button>
          </form>

          <p className="text-xs text-[#525252] mt-8 text-center">
            Access is invite-only. Contact your Studio Owner or Admin to request an account.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">{label}</label>
      <input data-testid={testid} {...props}
        className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2.5 text-sm outline-none transition-colors duration-150 placeholder:text-[#525252]" />
    </div>
  );
}
